import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon, jnjCloseNaked } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Callout } from "@jmc/solid-design-system/src/components/molecules/Callout/Callout";
import { Tabs } from "@jmc/solid-design-system/src/components/molecules/Tabs/Tabs";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import algoliasearch from "algoliasearch/lite";
import classnames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { IGatsbyImageData } from "gatsby-plugin-image";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Configure, InstantSearch } from "react-instantsearch-dom";
import { GoogleMapsLoader } from "react-instantsearch-dom-maps";

import { Controls } from "./Controls/Controls";
import { Geo } from "./Geo/Geo";
import { InfoPanel } from "./InfoPanel/InfoPanel";
import { LocatorContextProvider } from "./LocatorContext/LocatorContext";
import LocatorGrid from "./LocatorGrid/LocatorGrid";
import { NoResultsOverlay } from "./NoResultsOverlay/NoResultsOverlay";
import { Results } from "./Results/Results";
import style from "./style.module.scss";

interface Props {
    id: string;
    tag: string;
    distanceUnit: string;
    component?: JSX.Element;
    placement?: string;
    name?: string;
    indexLocation?: string;
    sectionBackground?: string;
    description?: string | JSX.Element;
    image?: string | IGatsbyImageData;
    loading?: "eager" | "lazy";
    noPaddingXl?: boolean;
}

const searchClient = algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_KEY);

export const Locator = ({
    id,
    tag,
    name,
    distanceUnit,
    placement,
    indexLocation,
    component = null,
    sectionBackground = null,
    description,
    image = null,
    loading = null,
    noPaddingXl = false,
}: Props): JSX.Element => {
    const [searchFrom, setSearchFrom] = useState("");
    const isMobile = useMediaQuery(BreakPoint.md);
    const isTablet = useMediaQuery(BreakPoint.lg);
    const { jnjFullBranded } = useJnjBranding();
    const { t } = useTranslation();
    const locale = useLocale();

    // We start with 1000km search range, so that we can show all the points on the map to start.
    // In order to limit this first pass, we'd need to pass the IP, which is problematic.
    // Starting with such a large range, also prevents us from needing to differentiate km vs mi in
    // this component.
    const initialSearchDistance = "100000";

    const [searchDistance, setSearchDistance] = useState(initialSearchDistance);
    const [keyword, setKeyword] = useState("");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [noAddress, setNoAddress] = useState(false);
    const [hideScroll, setHideScroll] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (isTablet) {
            if ((hideScroll || mobileOpen) && typeof window !== "undefined") {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "unset";
            }
        }
    }, [mobileOpen, hideScroll]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addressToCoords = (google: any, address: string, distance: string): void => {
        // set keyword in state because we only have coordinates inside algolia.
        setKeyword(address);

        if (address === "") {
            setNoAddress(false);
            setSearchFrom("");
            setSearchDistance(initialSearchDistance);
        } else {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { address: address },
                (results: { geometry: { location: { lat: () => unknown; lng: () => unknown } } }[], status: string) => {
                    if (status === "OK") {
                        setNoAddress(false);
                        const newLocation = `${results[0].geometry.location.lat()},${results[0].geometry.location.lng()}`;
                        setSearchFrom(newLocation);
                        setSearchDistance(distance || initialSearchDistance);
                    } else {
                        setNoAddress(true);
                        /* eslint-disable no-console */
                        console.error("Failed geocoding address.", status);
                    }
                },
            );
        }
    };

    const imageRatio = Math.min(Math.max(image?.height / image?.width, 0.75), 1.33);

    const calloutImage = image?.images ? (
        <GatsbyImage
            image={image}
            alt={""}
            loading={loading}
            style={{
                top: 0,
                insetInlineStart: 0,
                paddingBlockStart: `${imageRatio * 100}%`,
                height: 0,
            }}
        />
    ) : typeof image === "string" ? (
        <img
            src={image}
            loading={loading}
            alt={""}
            style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                top: 0,
                insetInlineStart: 0,
                objectFit: "cover",
            }}
        ></img>
    ) : null;

    return (
        <>
            {isTablet && jnjFullBranded && !mobileOpen && (
                <div className={style.mobileCalloutWrapper}>
                    <Callout
                        id={`${id}-callout`}
                        contentAlignment={"end"}
                        image={calloutImage}
                        title={name}
                        noPaddingXl={noPaddingXl}
                        footer={
                            <Button color="white" onClick={() => setMobileOpen(true)} fullWidth={isMobile}>
                                {t("Go to map", { ns: "common" })}
                            </Button>
                        }
                    >
                        {description}
                    </Callout>
                </div>
            )}
            <div
                className={classnames(
                    style.mapWrapper,
                    isTablet && jnjFullBranded && style.mobileOverlay,
                    mobileOpen && style.open,
                    sectionBackground === "gray" && style.gray,
                )}
                ref={ref}
            >
                {isTablet && jnjFullBranded && (
                    <button className={style.mobileClose} onClick={() => setMobileOpen(false)}>
                        <Icon color="interactive-05" size={"medium"} icon={jnjCloseNaked} type={"jnj"} />
                    </button>
                )}
                {!isTablet && description && <div className={style.description}>{description}</div>}
                <InstantSearch indexName={indexLocation} searchClient={searchClient}>
                    <Configure
                        hitsPerPage={100}
                        getRankingInfo
                        typoTolerance="min"
                        tagFilters={tag}
                        aroundLatLng={searchFrom}
                        aroundRadius={searchDistance}
                    />

                    <GoogleMapsLoader apiKey={process.env.GATSBY_GOOGLE_MAP_API_KEY}>
                        {(google: unknown) => {
                            return (
                                <LocatorContextProvider google={google}>
                                    <>
                                        <div
                                            className={classnames(style.panel, style.filtersPanel, style.infoPanelOpen)}
                                        >
                                            <Controls
                                                google={google}
                                                geocoder={addressToCoords}
                                                distanceLabel={distanceUnit as "km" | "mi"}
                                                name={name}
                                                placement={placement}
                                                sectionBackground={sectionBackground}
                                            />
                                            {jnjFullBranded && isTablet && (
                                                <div className={style.mobileTabs}>
                                                    <Tabs
                                                        selectedTab={tabIndex}
                                                        noSpacing
                                                        noTopSpacing
                                                        onChange={(tabIndex) => setTabIndex(tabIndex)}
                                                        locale={locale}
                                                    >
                                                        <Tabs.Tab title={t("Map", { ns: "common" })}></Tabs.Tab>
                                                        <Tabs.Tab title={t("List", { ns: "common" })}></Tabs.Tab>
                                                    </Tabs>
                                                </div>
                                            )}
                                        </div>
                                        <LocatorGrid>
                                            {
                                                <>
                                                    {(!(jnjFullBranded && isTablet) || tabIndex === 1 || noAddress) && (
                                                        <div
                                                            className={classnames(
                                                                style.resultsPanel,
                                                                hideScroll ? style.hideScroll : null,
                                                            )}
                                                        >
                                                            <Results
                                                                address={keyword}
                                                                name={name}
                                                                placement={placement}
                                                                distanceLabel={distanceUnit as "km" | "mi"}
                                                                google={google}
                                                                geocoder={addressToCoords}
                                                                locatorRef={ref}
                                                                sectionBackground={sectionBackground}
                                                                showMap={() => {
                                                                    isTablet && setTabIndex(0);
                                                                }}
                                                                noAddress={noAddress}
                                                            />
                                                        </div>
                                                    )}
                                                    <div
                                                        className={classnames(
                                                            style.mapPanel,
                                                            style.panel,
                                                            jnjFullBranded &&
                                                                ((isTablet && tabIndex !== 0) || noAddress)
                                                                ? style.hidden
                                                                : null,
                                                        )}
                                                    >
                                                        <InfoPanel
                                                            name={name}
                                                            placement={placement}
                                                            component={component}
                                                            hideScroll={(val: boolean) => setHideScroll(val)}
                                                        />
                                                        <Geo
                                                            searchFrom={searchFrom}
                                                            name={name}
                                                            placement={placement}
                                                            hideMap={
                                                                jnjFullBranded &&
                                                                ((isTablet && tabIndex !== 0) || noAddress)
                                                            }
                                                        />
                                                        {jnjFullBranded &&
                                                            keyword &&
                                                            (!isTablet || tabIndex === 0) &&
                                                            !noAddress && <NoResultsOverlay address={keyword} />}
                                                    </div>
                                                </>
                                            }
                                        </LocatorGrid>
                                    </>
                                </LocatorContextProvider>
                            );
                        }}
                    </GoogleMapsLoader>
                </InstantSearch>
            </div>
        </>
    );
};

export default Locator;
